import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useProperty } from "@/contexts/PropertyContext";
import { useSubscribe } from "@/contexts/RealtimeContext";
import { PageHeader } from "@/components/PageHelpers";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import MaintenancePhotos from "@/components/MaintenancePhotos";
import AdminTicketBidsSection from "@/components/AdminTicketBidsSection";
import AutoPickSettingsCard from "@/components/AutoPickSettingsCard";
import VendorSuggestBar from "@/components/VendorSuggestBar";

const PRIORITY_COLORS = {
  urgent: "bg-rose-500/20 text-rose-300 border-rose-500/40",
  high: "bg-orange-500/20 text-orange-300 border-orange-500/40",
  medium: "bg-yellow-500/20 text-yellow-300 border-yellow-500/40",
  low: "bg-zinc-500/20 text-zinc-300 border-zinc-500/40",
};
const STATUS_COLORS = {
  open: "bg-orange-500/20 text-orange-300 border-orange-500/40",
  in_progress: "bg-indigo-500/20 text-indigo-300 border-indigo-500/40",
  resolved: "bg-emerald-500/20 text-emerald-300 border-emerald-500/40",
};

export default function AdminMaintenancePage() {
  const { propertyId } = useProperty();
  const [items, setItems] = useState([]);
  const load = useCallback(async () => {
    if (!propertyId) return;
    const { data } = await api.get(`/maintenance?property_id=${propertyId}`);
    setItems(data.items);
  }, [propertyId]);
  useEffect(() => { load(); }, [load]);

  // Live sync — new tickets, bids landing, or status changes trigger a refetch.
  useSubscribe(propertyId ? `property:${propertyId}` : null, ({ event }) => {
    if (["ticket.new", "ticket.update", "bid.new", "bid.updated"].includes(event)) load();
  });

  const setStatus = async (id, status) => {
    await api.patch(`/maintenance/${id}`, { status });
    toast.success("Updated");
    load();
  };

  return (
    <div>
      <PageHeader eyebrow="Operations" title="Maintenance" subtitle="Every work order — including photos residents attached for faster bidding." />
      <AutoPickSettingsCard />
      <div className="space-y-3">
        {items.map((m) => (
          <Card key={m.id} className="p-5 border-white/10 bg-card" data-testid={`maint-row-${m.id}`}>
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="text-xs uppercase tracking-widest text-zinc-500">{m.category}</div>
                  <Badge variant="outline" className={PRIORITY_COLORS[m.priority]}>{m.priority}</Badge>
                  <Badge variant="outline" className={STATUS_COLORS[m.status]}>{m.status.replace("_"," ")}</Badge>
                  <div className="text-xs text-zinc-500">Unit {m.unit} · {m.resident_name}</div>
                </div>
                <div className="text-lg font-semibold font-display mt-1">{m.title}</div>
                <div className="text-sm text-zinc-300 mt-1">{m.description}</div>
                <MaintenancePhotos ticket={m} editable={false} />
                <VendorSuggestBar ticket={m} onAssigned={load} compact />
                <AdminTicketBidsSection ticket={m} onUpdate={load} />
              </div>
              <div className="shrink-0">
                <Select onValueChange={(v) => setStatus(m.id, v)}>
                  <SelectTrigger className="w-40" data-testid={`maint-status-${m.id}`}>
                    <SelectValue placeholder="Set status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="open">Open</SelectItem>
                    <SelectItem value="in_progress">In progress</SelectItem>
                    <SelectItem value="resolved">Resolved</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </Card>
        ))}
        {items.length === 0 && <div className="text-sm text-muted-foreground">No maintenance requests.</div>}
      </div>
    </div>
  );
}
